/**
 * Changing a password.
 *
 * Shared, because nothing on it is specific to either app: the endpoint, the
 * rules, the forced-visit case and the consequence are all properties of the
 * account, not of the door it signed in through. Both portals route this
 * component directly.
 *
 * Reachable from two places and it matters which: an ordinary visit from the
 * account menu, and a **forced** visit because the account still has the
 * password an administrator generated. The forced case is routed outside the
 * shell by both apps, because offering navigation to somebody who cannot use
 * any of it is offering a maze.
 *
 * Changing a password ends every other session. Said out loud beforehand, since
 * somebody with the app open on a second machine will otherwise find it signed
 * out and assume something broke.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound } from 'lucide-react';

import { api, ApiError, setAccessToken } from '../../lib/api-client';
import { useCurrentUser, useSession } from '../../auth/session';
import { toast } from '../ui/toast';
import { Button } from '../ui/button';
import { PasswordInput } from '../ui/input';
import { Alert, Card, CardBody, CardHeader } from '../ui/surfaces';

/**
 * Deliberately light on rules.
 *
 * Length and a confirmation, plus whatever the server says. Composition
 * requirements push people toward one predictable pattern, and a client-side
 * copy of the server's policy is a second place for it to be wrong — the server
 * refuses what this misses and its message is shown verbatim.
 */
const schemaFor = (hasPassword: boolean) =>
  z
    .object({
      // Not asked of a client that has only ever signed in with codes: it has no
      // current password, and it proved it holds its login when the code arrived.
      current_password: hasPassword
        ? z.string().min(1, 'Enter your current password.')
        : z.string(),
      new_password: z.string().min(10, 'Use at least 10 characters.'),
      confirm: z.string().min(1, 'Type the new password again.'),
    })
    .refine((values) => values.new_password === values.confirm, {
      // Attached to the confirmation field, which is the one to correct.
      path: ['confirm'],
      message: 'The two do not match.',
    })
    .refine((values) => values.new_password !== values.current_password, {
      path: ['new_password'],
      message: 'The new password must be different from the current one.',
    });

type FormValues = z.infer<ReturnType<typeof schemaFor>>;

export interface ChangePasswordPageProps {
  /** Where to go once the change has gone through. Defaults to the root. */
  doneTo?: string;
}

export function ChangePasswordPage({ doneTo = '/' }: ChangePasswordPageProps = {}) {
  const user = useCurrentUser();
  const refreshSession = useSession((state) => state.refreshSession);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const forced = user?.must_change_password ?? false;
  const hasPassword = user?.has_password ?? true;

  const form = useForm<FormValues>({
    resolver: zodResolver(schemaFor(hasPassword)),
    defaultValues: { current_password: '', new_password: '', confirm: '' },
  });

  const submit = async (values: FormValues) => {
    setBusy(true);
    try {
      const result = await api.post<{
        access_token: string;
        other_sessions_ended: number;
      }>('/auth/change-password', {
        ...(hasPassword ? { current_password: values.current_password } : {}),
        new_password: values.new_password,
      });
      // The change revoked every token the account had, including this
      // session's, and the server issued a replacement pair. Installing the new
      // access token is what keeps the promise this page makes — without it the
      // session works for a few more minutes and then signs out at the next
      // page load, which looks like a random failure.
      setAccessToken(result.access_token);
      // The session carries `must_change_password`, so it has to be re-read
      // before the guard will let this account anywhere else.
      await refreshSession();
      toast.success(
        'Password changed',
        result.other_sessions_ended > 0
          ? `${result.other_sessions_ended} other session${result.other_sessions_ended === 1 ? '' : 's'} signed out.`
          : 'You stay signed in here.',
      );
      navigate(doneTo, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        const fieldErrors = error.fieldErrors;
        if (Object.keys(fieldErrors).length > 0) {
          for (const [field, message] of Object.entries(fieldErrors)) {
            if (field === 'current_password' || field === 'new_password') {
              form.setError(field, { message });
            }
          }
        } else {
          // A policy refusal arrives as a plain message with no field — it
          // describes the new password, so that is where it belongs.
          form.setError('new_password', { message: error.message });
        }
      } else {
        form.setError('root', { message: 'Could not reach the server. Try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <Card>
        <CardHeader
          title={
            forced
              ? 'Choose your own password'
              : hasPassword
                ? 'Change your password'
                : 'Set a password'
          }
          description={
            forced
              ? 'This account still has the password that was generated for it. Set your own to continue.'
              : hasPassword
                ? undefined
                : 'You sign in with one-time codes. A password lets you sign in without waiting for one.'
          }
        />
        <CardBody>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit(submit)();
            }}
          >
            {forced ? (
              <Alert tone="warning" title="A generated password was handed over">
                It was issued once when the account was created and stored nowhere, but a password
                that has travelled by any other route should not stay in use.
              </Alert>
            ) : null}

            {form.formState.errors.root?.message ? (
              <Alert tone="danger">{form.formState.errors.root.message}</Alert>
            ) : null}

            {hasPassword ? (
              <PasswordInput
                label="Current password"
                autoComplete="current-password"
                autoFocus
                prefix={<KeyRound className="size-3.5" />}
                error={form.formState.errors.current_password?.message}
                {...form.register('current_password')}
              />
            ) : null}
            <PasswordInput
              label="New password"
              autoComplete="new-password"
              hint="At least 10 characters. Length beats complexity."
              error={form.formState.errors.new_password?.message}
              {...form.register('new_password')}
            />
            <PasswordInput
              label="New password again"
              autoComplete="new-password"
              error={form.formState.errors.confirm?.message}
              {...form.register('confirm')}
            />

            <p className="text-2xs leading-relaxed text-faint">
              Changing your password signs out every other session, on every device. This one stays
              signed in.
            </p>

            <Button type="submit" loading={busy} className="w-full justify-center">
              Change password
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
